// ============================================================================
// MealMarkerSheet — the calendar's MEAL day-marker detail panel (mobile-first).
// ----------------------------------------------------------------------------
// Tapping the amber meal marker on either calendar (trainer all-clients or
// client own) opens this sheet. A meal marker is a per-DAY summary (NOT one per
// meal), so this shows that day's total calories + the number of meals logged,
// in the amber provenance colour that matches the marker + CalendarLegend. On
// the trainer calendar it also names the client. Read-only — it mirrors the
// ActivityDetailSheet pattern. Strings live in the bilingual `nutrition`
// namespace; built on the same dashboard-local Sheet.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Avatar, Badge } from "../components";
import { cn } from "../lib/cn";
import { formatDayLong } from "../lib/date";
import { Sheet } from "./Sheet";
import { ENTRY_STYLE } from "./sessionStyle";
import type { MealMarkerVM } from "./viewModel";

export interface MealMarkerSheetProps {
  /** The day's meal marker to show; null closes the sheet. */
  marker: MealMarkerVM | null;
  onClose: () => void;
  /** Show the client name/avatar (trainer calendar). Hidden on the client's own. */
  showClient?: boolean;
}

/** Locale-aware long date for the marker's day; falls back to the raw day. */
function fmtDay(day: string): string {
  return formatDayLong(`${day}T00:00`) || day;
}

export function MealMarkerSheet({
  marker,
  onClose,
  showClient = false,
}: MealMarkerSheetProps) {
  const { t } = useTranslation("nutrition");
  const st = ENTRY_STYLE.meal;
  const kcal = marker ? Math.round(marker.kcal) : 0;

  return (
    <Sheet
      open={marker != null}
      onClose={onClose}
      size="md"
      title={
        marker ? (
          <span className="inline-flex items-center gap-2.5">
            <span
              className={cn("h-6 w-1.5 shrink-0 rounded-pill", st.grad)}
              aria-hidden
            />
            {t("marker.title")}
          </span>
        ) : (
          ""
        )
      }
      subtitle={marker ? fmtDay(marker.day) : undefined}
    >
      {!marker ? null : (
        <div className="space-y-4 py-1">
          {/* Client (trainer calendar only) */}
          {showClient && marker.clientName && (
            <div className="flex items-center gap-3">
              <Avatar name={marker.clientName} size="md" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-base font-bold text-body">
                  {marker.clientName}
                </p>
                <p className="text-sm text-body-soft">{t("marker.title")}</p>
              </div>
            </div>
          )}

          {/* Big amber total + meal count */}
          <div
            className={cn(
              "rounded-md p-5 text-center ring-1 ring-line",
              st.soft,
            )}
          >
            <p className="font-display text-4xl font-extrabold tabular-nums text-body">
              {kcal.toLocaleString()}
              <span className="ml-1.5 align-baseline text-lg font-bold text-body-soft">
                {t("macro.kcalUnit")}
              </span>
            </p>
            <Badge tone={st.tone} dot className="mt-3">
              {marker.count > 0
                ? t("marker.mealCount", { count: marker.count })
                : t("marker.title")}
            </Badge>
          </div>
        </div>
      )}
    </Sheet>
  );
}
